/**
 * Core analytics utility for Mixpanel and other tracking.
 * Expects window.fhsAnalytics.context to be set by html-body-tracking.twig before this runs.
 */

import {
  trackComponentInteraction,
  getComponentContext,
  INTERACTION_TYPES,
} from './component-interaction-tracker.js';

/**
 * Parse UTM and related params from current URL (for SPA or client-side updates).
 * @returns {{ utm_source?: string, utm_medium?: string, utm_campaign?: string }}
 */
function getUtmFromUrl() {
  if (
    typeof window === 'undefined' ||
    !window.location ||
    !window.location.search
  ) {
    return {};
  }
  const params = new URLSearchParams(window.location.search);
  const utm_source = params.get('utm_source') || '';
  const utm_medium = params.get('utm_medium') || '';
  const utm_campaign = params.get('utm_campaign') || '';
  return { utm_source, utm_medium, utm_campaign };
}

/** Set UTM params on window.fhsAnalytics.context from the current URL. */
function init() {
  const win = typeof window !== 'undefined' ? window : null;
  if (!win) return;

  win.fhsAnalytics = win.fhsAnalytics || {};
  win.fhsAnalytics.context = win.fhsAnalytics.context || {};
  const ctx = win.fhsAnalytics.context;

  const utm = getUtmFromUrl();
  if (utm.utm_source) ctx.utm_source = utm.utm_source;
  if (utm.utm_medium) ctx.utm_medium = utm.utm_medium;
  if (utm.utm_campaign) ctx.utm_campaign = utm.utm_campaign;
}

/**
 * Get full context (shared + optional component context).
 * @param {Object} [componentContext] - Optional { component_type, component_id, pattern_id }
 * @returns {Object}
 */
export function getContext(componentContext = {}) {
  if (
    typeof window !== 'undefined' &&
    window.fhsAnalytics &&
    window.fhsAnalytics.context
  ) {
    return { ...window.fhsAnalytics.context, ...componentContext };
  }
  return { ...componentContext };
}

/**
 * Remove undefined, null, and empty string values from an object
 * @param {Object} obj
 * @returns {Object}
 */
function filterProperties(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([key, value]) => value !== undefined && value !== null && value !== '',
    ),
  );
}

/**
 * Send a single event to Mixpanel. Called when Mixpanel is ready (real library loaded).
 */
function sendToMixpanel(eventName, context) {
  if (typeof window === 'undefined' || !window.mixpanel) return;
  const mp = window.mixpanel.fareharbor || window.mixpanel;
  if (typeof mp.track === 'function') {
    mp.track(eventName, context);
  }
}

/**
 * Track an event with shared context merged in. If Mixpanel is not loaded yet, queues
 * the call and sends when mixpanel:loaded fires so debug and batching work correctly.
 * @param {string} eventName
 * @param {Object} [properties] - Event-specific properties; component_type, component_id, pattern_id can go here
 */
export function track(eventName, properties = {}) {
  const context = filterProperties(getContext(properties));
  const send = () => sendToMixpanel(eventName, context);

  if (window.__mixpanelFareharborLoaded) {
    send();
  } else {
    window.addEventListener('mixpanel:loaded', send, { once: true });
  }
}

// Initialize on load so context is available to other scripts
init();

// Export for use in app bundle and block scripts
if (typeof window !== 'undefined') {
  window.fhsAnalytics = window.fhsAnalytics || {};
  window.fhsAnalytics.getContext = getContext;
  window.fhsAnalytics.track = track;
  window.fhsAnalytics.getComponentContext = getComponentContext;
  window.fhsAnalytics.trackComponentInteraction = trackComponentInteraction;
  window.fhsAnalytics.INTERACTION_TYPES = INTERACTION_TYPES;
  // Flush any events queued before this script ran
  const queue = window.__fhsAnalyticsQueue;
  if (queue?.length) {
    window.__fhsAnalyticsQueue = [];
    queue.forEach(([eventName, properties]) =>
      track(eventName, properties || {}),
    );
  }
}
